import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { copyTextToClipboard, shareURL } from '@tma.js/sdk-react';
import { Check, Copy, Send, Swords } from 'lucide-react';

import { env } from '@/env';
import type { CpInviteController } from '@/hooks/useCpInvite';

interface CpInviteSheetProps {
  invite: CpInviteController;
  /** Close the sheet (also cancels a pending invite via the parent). */
  onClose: () => void;
}

/** m:ss countdown to the invite's `expiresAt`. */
function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Creator-side friend-challenge sheet for Chinese Poker (OFC).
 *
 * Flow: tap "Create invite" → server mints a code → the big code is shown
 * with Share (Telegram contact picker, share-as-text) + Copy fallback →
 * countdown to expiry + cancel while waiting.
 *
 * No matchLength picker: OFC has none (contrast with QGammon which has 1/3/5).
 *
 * Share: share-as-text only — `shareURL` opens Telegram's native contact
 * picker with a message that carries the code + instructions to paste it into
 * "I have an invite code". No deep-link / startapp plumbing.
 *
 * 409 limit_reached: rendered inline with a clear message so the user knows
 * they have an active invite somewhere (they can cancel it in another session).
 */
export function CpInviteSheet({ invite, onClose }: CpInviteSheetProps) {
  const { t } = useTranslation('chinesepoker');
  const { status, pending, accepted, error, create, cancel } = invite;

  const [copied, setCopied] = useState(false);
  const [remaining, setRemaining] = useState(0);

  // Auto-create on mount: open sheet → immediately mint an invite.
  // Intentionally run only once (empty deps): `create` is stable (useCallback),
  // and re-running on every render would re-POST. The `status === 'idle'` guard
  // is a safety net in case the sheet unmounts-remounts mid-flight.
  const createRef = useRef(create);
  createRef.current = create;
  useEffect(() => {
    createRef.current();
  }, []);

  // Countdown to expiry while an invite is pending.
  useEffect(() => {
    if (!pending) {
      setRemaining(0);
      return;
    }
    const target = Date.parse(pending.expiresAt);
    const tick = () => setRemaining(target - Date.now());
    tick();
    const id = window.setInterval(tick, 1_000);
    return () => window.clearInterval(id);
  }, [pending]);

  // Reset the "copied" flash whenever the code changes.
  useEffect(() => {
    setCopied(false);
  }, [pending?.code]);

  function close() {
    if (pending && status !== 'accepted') cancel(pending.code);
    onClose();
  }

  function shareToTelegram() {
    if (!pending) return;
    // Share-as-text: open the app link with a prefilled message that includes
    // the code + instructions to paste it into "I have an invite code".
    const appLink = `https://t.me/${env.telegramBotUsername}`;
    const text = t('invite.shareText', { code: pending.code, app: appLink });
    try {
      shareURL(appLink, text);
    } catch {
      /* not in Telegram — Copy remains available */
    }
  }

  async function copyCode() {
    if (!pending) return;
    try {
      await copyTextToClipboard(pending.code);
    } catch {
      try {
        await navigator.clipboard.writeText(pending.code);
      } catch {
        /* clipboard unavailable */
      }
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1_500);
  }

  // While `accepted` the parent navigates immediately; render a tiny confirm
  // frame so the sheet doesn't flash empty during the transition.
  const showAccepted = status === 'accepted' && accepted;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label={t('invite.close')}
        onClick={close}
        className="absolute inset-0 bg-black/60"
      />
      <div className="relative w-full max-w-md rounded-t-2xl bg-bg-secondary p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <div className="mb-3 flex items-center gap-2">
          <h2 className="flex items-center gap-2 text-base font-bold text-fg">
            <Swords className="h-4 w-4 text-brand" />
            {t('invite.title')}
          </h2>
        </div>

        {showAccepted ? (
          /* ── accepted: brief confirmation before the parent navigates ───── */
          <div className="flex flex-col items-center gap-4 py-6">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand/15 text-brand">
              <Check className="h-6 w-6" />
            </span>
            <p className="text-center text-base font-bold text-fg">
              {t('invite.acceptedBy', { name: accepted.opponent.displayName })}
            </p>
          </div>
        ) : pending ? (
          /* ── pending: the code + share/copy + countdown + cancel ────────── */
          <div className="flex flex-col items-center gap-4 py-2">
            <p className="text-xs font-medium text-fg-muted">{t('invite.codeLabel')}</p>
            <div className="select-all rounded-xl border border-border bg-bg px-4 py-3 text-center text-2xl font-bold tracking-[0.25em] text-fg tabular-nums">
              {pending.code}
            </div>

            <div className="flex w-full gap-2">
              <button
                type="button"
                onClick={shareToTelegram}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-brand py-3 text-sm font-bold text-brand-fg transition-opacity active:opacity-80"
              >
                <Send className="h-4 w-4" />
                {t('invite.share')}
              </button>
              <button
                type="button"
                onClick={copyCode}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-bg-tertiary py-3 text-sm font-bold text-fg transition-opacity active:opacity-80"
              >
                {copied ? <Check className="h-4 w-4 text-brand" /> : <Copy className="h-4 w-4" />}
                {copied ? t('invite.copied') : t('invite.copy')}
              </button>
            </div>

            <p className="text-xs tabular-nums text-fg-muted">
              {t('invite.waiting')} ({t('invite.expires', { time: formatRemaining(remaining) })})
            </p>

            <button
              type="button"
              onClick={close}
              className="w-full rounded-xl bg-bg-tertiary py-3 text-sm font-bold text-fg-muted transition-opacity active:opacity-80"
            >
              {t('invite.cancel')}
            </button>
          </div>
        ) : (
          /* ── idle / creating / error ─────────────────────────────────────── */
          <div className="flex flex-col gap-4 py-2">
            {error === 'limit' && (
              <p className="rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger">
                {t('invite.limitReached')}
              </p>
            )}
            {error === 'failed' && (
              <p className="text-sm text-danger">{t('invite.createError')}</p>
            )}

            <button
              type="button"
              onClick={create}
              disabled={status === 'creating'}
              className="w-full rounded-xl bg-brand py-3 text-sm font-bold text-brand-fg transition-opacity active:opacity-80 disabled:opacity-60"
            >
              {status === 'creating' ? t('invite.creating') : t('invite.create')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
